import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import TripList from 'components/search/TripList';
import { AFTER_MIDNIGHT_VARIANTS } from 'utils/afterMidnightVariant';
import AfterMidnightDivider from './AfterMidnightDivider';

/** No-op placeholder for the optional callbacks and for `TripList`'s filter reset. */
const NOOP = () => {
  // Deliberately empty, and hoisted: this way the section never hands `TripList` — or any of the
  // optional callbacks below — a fresh function identity on every render. `TripList` only reaches
  // it through the no-results branch, which `isNotFiltered` suppresses here.
};

/**
 * Section holding the small-hours departures of the day after the one searched.
 *
 * Renders if and only if the payload carried trips — there is no funnel-side flag for that, exactly
 * as with closed trips: the feature is gated server-side per brand, and a second on/off here would
 * create a state (backend emitting, funnel hiding) invisible from both ends. No skeleton and no
 * empty state either: with nothing to show the page is what it is today.
 *
 * The cards come from `TripList`, the listing's own component, so "same format as the main listing"
 * is structural instead of copied — including the round-trip pricing chain. Its props are spelled
 * out rather than spread, because `TripList` carries effects a second instance would trip over.
 *
 * @param {object} props - Component props.
 * @param {Array} props.trips - Section trips, already parsed, ordered and provider-scoped.
 * @param {string} props.windowFrom - Start of the window, for the condensed divider.
 * @param {'prominent'|'soft'|'minimal'} props.variant - Divider presentation.
 * @param {Function} props.selectTrip - The listing's real selection handler.
 * @param {boolean} props.roundTrip - Whether the search is a round trip.
 * @param {boolean} props.isLogged - Whether the user is logged in.
 * @param {object} props.filtersApplied - Applied filters, for the cards' own reporting.
 * @param {object} props.departureTrip - Chosen outbound, on a round trip.
 * @param {object} props.paymentPlans - Payment plans for the cards.
 * @param {number} props.installmentsMinAmount - Minimum amount for installments.
 * @param {Function} props.onOptionClick - Card option handler.
 * @param {Function} props.onQuickPurchaseRequest - Quick purchase handler.
 * @param {string} props.couponCode - Applied coupon code.
 * @param {Array} props.discountedTripsByCoupon - Trips discounted by the coupon.
 * @param {Function} props.onImpression - Called when the section is on screen, with what to report.
 * @returns {JSX.Element|null} The section, or null when there is nothing to show.
 */
const AfterMidnightSection = ({
  trips,
  windowFrom,
  variant,
  selectTrip,
  roundTrip,
  isLogged,
  filtersApplied,
  departureTrip,
  paymentPlans,
  installmentsMinAmount,
  onOptionClick,
  onQuickPurchaseRequest,
  couponCode,
  discountedTripsByCoupon,
  onImpression,
}) => {
  const hasTrips = Boolean(trips?.length);
  const sectionDate = hasTrips ? trips[0].departure : null;

  // Checked rather than assumed, because the failure is silent and convincing: `moment(undefined)`
  // and `moment('not a date')` are not invalid dates, the first resolves to NOW. An unguarded
  // format would print today's date on a section about the small hours, and stamp the same wrong
  // day on the impression event. The parser already rejects trips with no `departure`; this is the
  // second lock, on the value the heading and the event actually read.
  const hasValidDate = Boolean(sectionDate) && moment(sectionDate).isValid();

  // Reports what is on screen; it does NOT decide whether this is a repeat. That belongs to the
  // listing, which is what survives the ternary that remounts this component — see
  // `reportAfterMidnightImpression` there. Calling on every run is safe and deliberate: the owner
  // de-duplicates, and a guard here would be reborn with each remount.
  useEffect(() => {
    if (!hasTrips || !hasValidDate) return;

    onImpression({
      tripCount: trips.length,
      variant,
      date: moment(sectionDate).format('YYYY-MM-DD'),
    });
  }, [hasTrips, hasValidDate, trips, variant, sectionDate, onImpression]);

  // No date, no section: the divider IS the heading and it is built from this value, so rendering
  // it with a bad date would ship the wrong claim rather than no claim.
  if (!hasTrips || !hasValidDate) return null;

  return (
    <div className="trip-container" data-testid="after-midnight-section">
      <AfterMidnightDivider date={sectionDate} windowFrom={windowFrom} variant={variant} />

      <TripList
        trips={trips}
        way="departure"
        transportType="bus"
        selectTrip={selectTrip}
        roundTrip={roundTrip}
        departureTrip={departureTrip}
        isLogged={isLogged}
        filtersApplied={filtersApplied}
        paymentPlans={paymentPlans}
        installmentsMinAmount={installmentsMinAmount}
        onOptionClick={onOptionClick}
        onQuickPurchaseRequest={onQuickPurchaseRequest}
        couponCode={couponCode}
        discountedTripsByCoupon={discountedTripsByCoupon}
        // The listing already filled the `results` ad space; a second instance would ask for its own.
        isSecondaryList
        // Suppresses the no-results branch and the passengers-not-found alert, neither of which
        // belongs to a section that is never filtered.
        isNotFiltered
        // Pinned, not inherited. `isNotFiltered` does NOT suppress the "trips filtered by schedule"
        // message, so spreading the parent's filter state would print it inside the section; and on
        // the open-ticket route the unfiltered branch returns null into a component that slices it.
        isOpenTicketList={false}
        activeDepartureFilter={false}
        activeLocationFilter={false}
        tooManyFilters={false}
        isAdLoading={false}
        resetTripFilters={NOOP}
      />
    </div>
  );
};

AfterMidnightSection.propTypes = {
  trips: PropTypes.array,
  windowFrom: PropTypes.string,
  variant: PropTypes.oneOf(AFTER_MIDNIGHT_VARIANTS),
  selectTrip: PropTypes.func.isRequired,
  roundTrip: PropTypes.bool,
  isLogged: PropTypes.bool,
  filtersApplied: PropTypes.object,
  departureTrip: PropTypes.object,
  paymentPlans: PropTypes.objectOf(PropTypes.number),
  installmentsMinAmount: PropTypes.number,
  onOptionClick: PropTypes.func,
  onQuickPurchaseRequest: PropTypes.func,
  couponCode: PropTypes.string,
  discountedTripsByCoupon: PropTypes.array,
  onImpression: PropTypes.func,
};

AfterMidnightSection.defaultProps = {
  trips: [],
  windowFrom: null,
  variant: undefined,
  roundTrip: false,
  isLogged: false,
  filtersApplied: {},
  departureTrip: null,
  paymentPlans: {},
  installmentsMinAmount: 0,
  onOptionClick: NOOP,
  onQuickPurchaseRequest: NOOP,
  couponCode: '',
  discountedTripsByCoupon: [],
  onImpression: NOOP,
};

export default AfterMidnightSection;
